import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, StarIcon, MessageCircleIcon, LinkIcon, RefreshCwIcon, ShieldCheckIcon, TrendingUpIcon, ThumbsUpIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function ReputationManagementPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1798">
      <SEOHead title="Reputation Management | Automate Reviews | Viridia Analytics" description="Turn happy customers into public proof. Manage reviews across platforms, automate review requests, and build trust for your local service business." canonical="/reputation-management" keywords="reputation management Phoenix, automated review requests, get more Google reviews, local business reputation, review management software" data-id="element-1799" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-1800">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1801">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-500/10 blur-[120px] animate-pulse-slow" data-id="element-1802"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1803">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1804">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-1805">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-1806">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm font-semibold tracking-wide mb-6" data-id="element-1807">
                  REPUTATION MANAGEMENT
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1808">
                  Turn Happy Customers Into{' '}
                  <span className="text-yellow-500" data-id="element-1809">Public Proof</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1810">
                  Manage your reviews across major platforms, automate review
                  requests after completed jobs, and maintain a stronger online
                  reputation that supports more conversions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1811">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-1812">
                    Book a Strategy Call
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1813" />
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-1814">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Reputation Control Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-1815">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-viridia-surface rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6 flex flex-col" data-id="element-1816">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-1817">
                  <div className="text-sm font-bold text-white flex items-center" data-id="element-1818">
                    <StarIcon className="w-4 h-4 text-yellow-500 mr-2 fill-current" data-id="element-1819" />
                    Reputation Control Center
                  </div>
                  <div className="bg-green-500/10 text-green-400 text-xs px-2 py-1 rounded font-medium" data-id="element-1820">
                    System Active
                  </div>
                </div>

                <div className="flex-grow relative" data-id="element-1821">
                  {/* Floating Review Cards */}
                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="absolute top-4 left-4 right-12 bg-white rounded-lg p-4 shadow-lg z-20" data-id="element-1822">
                    <div className="flex justify-between items-start mb-2" data-id="element-1823">
                      <div className="flex text-yellow-400" data-id="element-1824">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-1825" />)}
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-1826">
                        Google • 2m ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium" data-id="element-1827">
                      "They showed up on time and fixed the AC in under an hour.
                      Highly recommend!"
                    </p>
                    <div className="mt-3 flex items-center text-xs text-viridia font-semibold" data-id="element-1828">
                      <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-1829" /> Auto-reply
                      sent
                    </div>
                  </motion.div>

                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.8
                }} className="absolute top-32 left-12 right-4 bg-white rounded-lg p-4 shadow-lg z-10 opacity-90" data-id="element-1830">
                    <div className="flex justify-between items-start mb-2" data-id="element-1831">
                      <div className="flex text-yellow-400" data-id="element-1832">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-1833" />)}
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-1834">
                        Yelp • 1h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium" data-id="element-1835">
                      "Great communication from start to finish."
                    </p>
                  </motion.div>

                  {/* Automation Flow Graphic */}
                  <div className="absolute bottom-4 left-4 right-4 bg-viridia-black/80 backdrop-blur border border-gray-800 rounded-xl p-4" data-id="element-1836">
                    <div className="text-xs text-gray-400 mb-3 uppercase tracking-wider font-bold" data-id="element-1837">
                      Active Automation
                    </div>
                    <div className="flex items-center justify-between" data-id="element-1838">
                      <div className="text-center" data-id="element-1839">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-1" data-id="element-1840">
                          <CheckCircleIcon className="w-4 h-4 text-blue-400" data-id="element-1841" />
                        </div>
                        <span className="text-[10px] text-gray-400" data-id="element-1842">
                          Job Done
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-gray-700 mx-2 relative" data-id="element-1843">
                        <motion.div className="absolute top-1/2 left-0 w-2 h-2 bg-viridia rounded-full -translate-y-1/2" animate={{
                        left: ['0%', '100%']
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear'
                      }} data-id="element-1844" />
                      </div>
                      <div className="text-center" data-id="element-1845">
                        <div className="w-8 h-8 rounded-full bg-viridia/20 flex items-center justify-center mx-auto mb-1" data-id="element-1846">
                          <MessageCircleIcon className="w-4 h-4 text-viridia" data-id="element-1847" />
                        </div>
                        <span className="text-[10px] text-gray-400" data-id="element-1848">
                          SMS Sent
                        </span>
                      </div>
                      <div className="flex-1 h-px bg-gray-700 mx-2" data-id="element-1849"></div>
                      <div className="text-center" data-id="element-1850">
                        <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-1" data-id="element-1851">
                          <StarIcon className="w-4 h-4 text-yellow-500 fill-current" data-id="element-1852" />
                        </div>
                        <span className="text-[10px] text-gray-400" data-id="element-1853">
                          Review
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY REPUTATION MATTERS */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1854">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1855">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1856">
            <ScrollReveal data-id="element-1857">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1858">
                Reputation is a Sales System, Not Just a Branding Exercise
              </h2>
              <div className="space-y-6 text-gray-400 text-lg leading-relaxed" data-id="element-1859">
                <p data-id="element-1860">
                  Reviews influence trust before a prospect ever picks up the
                  phone. When a homeowner searches for a service, they don't
                  just look at who is closest — they look at who has the most
                  stars.
                </p>
                <p data-id="element-1861">
                  Good businesses often underperform online simply because they
                  do not consistently ask for reviews or manage responses. It's
                  not that customers are unhappy; it's that there is no system
                  in place to capture their positive experience.
                </p>
                <p data-id="element-1862">
                  Your reputation directly impacts your click-through rate and
                  conversion rate. We treat it like the growth engine it is.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-1863">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 relative" data-id="element-1864">
                <div className="absolute -top-4 -right-4 bg-yellow-500 text-black font-bold px-4 py-2 rounded-lg transform rotate-3 shadow-lg" data-id="element-1865">
                  Trust = Conversions
                </div>
                <div className="space-y-6" data-id="element-1866">
                  <div className="flex items-start gap-4" data-id="element-1867">
                    <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-1868">
                      <ThumbsUpIcon className="w-6 h-6 text-viridia" data-id="element-1869" />
                    </div>
                    <div data-id="element-1870">
                      <h4 className="text-white font-bold text-lg mb-1" data-id="element-1871">
                        Higher Click-Through
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1872">
                        Profiles with 4.5+ stars and recent reviews get
                        significantly more clicks from the Google Map Pack.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-1873">
                    <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-1874">
                      <TrendingUpIcon className="w-6 h-6 text-viridia" data-id="element-1875" />
                    </div>
                    <div data-id="element-1876">
                      <h4 className="text-white font-bold text-lg mb-1" data-id="element-1877">
                        Easier Sales
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1878">
                        When prospects read positive reviews before calling,
                        price resistance drops and trust is already established.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHAT WE HELP WITH */}
      <section className="py-24 bg-viridia-black" data-id="element-1879">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1880">
          <ScrollReveal data-id="element-1881">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1882">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1883">
                How We Build Your Reputation
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1884">
                Response automation and follow-up systems help you stay on top
                of reviews without creating more manual work.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1885">
            {[{
            icon: <RefreshCwIcon className="w-6 h-6" data-id="element-1886" />,
            title: 'Automated Requests',
            desc: 'Automatically trigger a review request via text or email the moment a job is marked complete in the CRM.'
          }, {
            icon: <LinkIcon className="w-6 h-6" data-id="element-1887" />,
            title: 'Simple Review Links',
            desc: 'We create frictionless, one-click links that send customers directly to the review page, removing all guesswork.'
          }, {
            icon: <MessageCircleIcon className="w-6 h-6" data-id="element-1888" />,
            title: 'Response Management',
            desc: 'Respond to reviews more consistently to show future prospects that you are active, attentive, and professional.'
          }, {
            icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-1889" />,
            title: 'Platform Monitoring',
            desc: 'Keep an eye on your presence across multiple platforms from one central dashboard.'
          }, {
            icon: <StarIcon className="w-6 h-6" data-id="element-1890" />,
            title: 'Trust Signals',
            desc: 'Turn positive customer experiences into long-term trust signals that live permanently on your profiles.'
          }, {
            icon: <CheckCircleIcon className="w-6 h-6" data-id="element-1891" />,
            title: 'Professional Activity',
            desc: 'Help your business stay active and relevant across major directories so you never look "closed" online.'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-1892">
                <div className="bg-viridia-surface rounded-xl p-8 border border-gray-800 h-full hover:border-yellow-500/30 transition-colors duration-300 group" data-id="element-1893">
                  <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center text-yellow-500 mb-6 group-hover:bg-yellow-500/20 transition-colors" data-id="element-1894">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1895">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1896">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PLATFORM MANAGEMENT & TIMING */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-1897">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1898">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-1899">
            {/* Multi-Platform */}
            <ScrollReveal data-id="element-1900">
              <div className="mb-12" data-id="element-1901">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-1902">
                  Every Platform Matters
                </h3>
                <p className="text-gray-400 mb-8" data-id="element-1903">
                  This isn't just about Google. Your customers are checking
                  Yelp, Facebook, and industry-specific directories. We help you
                  watch and manage your reputation across the platforms that
                  matter to your specific market.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-1904">
                  {['Google Business', 'Yelp', 'Facebook', 'Industry Directories'].map((platform, i) => <div key={i} className="px-4 py-2 rounded-full bg-viridia-black border border-gray-800 text-gray-300 text-sm font-medium flex items-center" data-id="element-1905">
                      <StarIcon className="w-3 h-3 text-yellow-500 mr-2 fill-current" data-id="element-1906" />{' '}
                      {platform}
                    </div>)}
                </div>
              </div>
            </ScrollReveal>

            {/* Timing & Automation */}
            <ScrollReveal delay={0.2} data-id="element-1907">
              <div data-id="element-1908">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-1909">
                  Timing is Everything
                </h3>
                <p className="text-gray-400 mb-8" data-id="element-1910">
                  The biggest driver of better reviews is timing. You are
                  significantly more likely to get a review when the request is
                  sent right after a completed job, while the positive
                  experience is still fresh.
                </p>

                {/* Timeline Visual */}
                <div className="bg-viridia-black rounded-xl p-6 border border-gray-800" data-id="element-1911">
                  <div className="flex items-center justify-between relative" data-id="element-1912">
                    <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-gray-800 -translate-y-1/2 z-0" data-id="element-1913"></div>

                    <div className="relative z-10 flex flex-col items-center" data-id="element-1914">
                      <div className="w-10 h-10 rounded-full bg-viridia text-viridia-black flex items-center justify-center font-bold mb-2 shadow-[0_0_15px_rgba(16,185,129,0.3)]" data-id="element-1915">
                        1
                      </div>
                      <span className="text-xs text-gray-300 font-medium" data-id="element-1916">
                        Job Complete
                      </span>
                    </div>

                    <div className="relative z-10 flex flex-col items-center" data-id="element-1917">
                      <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold mb-2 shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-id="element-1918">
                        2
                      </div>
                      <span className="text-xs text-gray-300 font-medium" data-id="element-1919">
                        Auto-SMS Sent
                      </span>
                    </div>

                    <div className="relative z-10 flex flex-col items-center" data-id="element-1920">
                      <div className="w-10 h-10 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold mb-2 shadow-[0_0_15px_rgba(234,179,8,0.3)]" data-id="element-1921">
                        3
                      </div>
                      <span className="text-xs text-gray-300 font-medium" data-id="element-1922">
                        5-Star Review
                      </span>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-6 italic" data-id="element-1923">
                    We set up the automated flows so you ask consistently
                    instead of relying on memory.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* PACKAGE LADDER CONNECTION */}
      <section className="py-20 bg-viridia-black" data-id="element-1924">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1925">
          <ScrollReveal data-id="element-1926">
            <div className="text-center" data-id="element-1927">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" data-id="element-1928">
                The Next Level of Growth
              </h2>
              <p className="text-lg text-gray-400 mb-8" data-id="element-1929">
                Basic CRM and lead response are the foundation. But for
                businesses that want stronger automation, long-term visibility,
                and market dominance, reputation management is the advanced
                capability that separates the leaders from the pack.
              </p>
              <p className="text-viridia font-medium" data-id="element-1930">
                Included in our Full Automation Suite.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-1931">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1932"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1933">
          <ScrollReveal data-id="element-1934">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1935">
              Ready to Build a Better Reputation?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1936">
              Strengthen trust, improve consistency, and build a system for
              turning happy customers into reviews that win more business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1937">
              <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1938">
                Book a Strategy Call
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1939" />
              </Link>
              <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border-2 border-white text-white font-bold text-lg hover:bg-white/10 transition-colors duration-300" data-id="element-1940">
                View Packages
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}